import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
} from '@material-ui/core';
import type { WehaGlobalSettings, WehaLine } from '../../Reducers/Configurator/types';
import { useTheme } from '@material-ui/core/styles';
import CustomLabel from './CustomLabel';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import {
  getFontByIdentifier,
  getCrossingSizesForFont,
  getCommonFontTypesForLines,
  resolveFontType,
} from '../../Reducers/Configurator/fontData';
import { selectWehaFontData } from '../../Reducers/Configurator/Selectors';
import { FONT_TYPE_SINGLE_LETTERS, FONT_TYPE_LETTERING } from '../../constants/fontTypes';
import { makeStyles } from '@material-ui/core/styles';
import { getFontUrl, createFontFacesCss } from '../../utils/fontUrls';
import SelectCrossingSizeDialog, { formatCrossingSizeLabel } from './SelectCrossingSizeDialog';

const useStyles = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
});

/** CSS font-family for a font identifier (weha font or fallback to theme default). */
function getFontFamilyForIdentifier(fontIdentifier: string, defaultFont: string): string {
  const baseId = fontIdentifier.split(' ')[0];
  if (getFontUrl(fontIdentifier)) {
    return `'${baseId}', ${defaultFont}`;
  }
  return defaultFont;
}

interface GeneralTextSettingsProps {
  globalSettings: WehaGlobalSettings;
  lines: WehaLine[];
  /** Apply multiple setting changes in one update (single Redux dispatch). */
  onChange: (changes: Partial<WehaGlobalSettings>) => void;
  onCrossingSizeDialogOpenChange?: (open: boolean) => void;
}

function getFontType(gs: WehaGlobalSettings): string {
  return gs.fontType ?? FONT_TYPE_SINGLE_LETTERS;
}

function getMaterial(gs: WehaGlobalSettings): string {
  if (gs.material) return gs.material;
  return 'Bronze';
}

function getAlignment(gs: WehaGlobalSettings): string {
  return gs.alignment || 'center';
}

const defaultAppFont = '"Open Sans", "Helvetica Neue", Arial, sans-serif';

const EXECUTION_LABEL_KEYS: Record<string, string> = {
  [FONT_TYPE_LETTERING]: 'weha.globalSettings.lettering',
  [FONT_TYPE_SINGLE_LETTERS]: 'weha.globalSettings.individualLetters',
};

export default function GeneralTextSettings({
  globalSettings,
  lines,
  onChange,
  onCrossingSizeDialogOpenChange,
}: GeneralTextSettingsProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const fontData = useSelector(selectWehaFontData);
  const [localComment, setLocalComment] = React.useState(globalSettings.comment ?? '');
  React.useEffect(() => {
    setLocalComment(globalSettings.comment ?? '');
  }, [globalSettings.comment]);
  const fontFamily = globalSettings.fontFamily ?? fontData[0]?.identifier ?? '';
  const appFont = theme.typography?.fontFamily || defaultAppFont;

  const alignment = getAlignment(globalSettings);
  const material = getMaterial(globalSettings);
  const selectedFont = getFontByIdentifier(fontData, fontFamily);
  const hasDowelXxl = Boolean(selectedFont?.dowelXxl);
  const extraLongDowel = Boolean(globalSettings?.extraLongDowel);
  const availableFontTypes = React.useMemo(
    () => getCommonFontTypesForLines(fontData, fontFamily, lines),
    [fontData, fontFamily, lines]
  );
  const executionOptions = availableFontTypes.map((value) => ({
    value,
    labelKey: EXECUTION_LABEL_KEYS[value] ?? value,
  }));
  const currentFontType = getFontType(globalSettings);
  const effectiveFontType = resolveFontType(currentFontType, availableFontTypes);
  const materialTypes = selectedFont?.materialTypes ?? [];
  const effectiveMaterial =
    materialTypes.find((m) => m.identifier.toLowerCase() === (material || '').toLowerCase())?.identifier ??
    materialTypes[0]?.identifier ??
    '';

  const crossingSizes = getCrossingSizesForFont(fontData, fontFamily);
  const hasCrossingSizes = crossingSizes.length > 0;
  const currentCrossingSize = globalSettings.crossingSize ?? null;
  const isCurrentCrossingValid =
    currentCrossingSize != null &&
    crossingSizes.some(
      (s) => s.height === currentCrossingSize.height && s.width === currentCrossingSize.width
    );
  const effectiveCrossingSize = isCurrentCrossingValid ? currentCrossingSize : null;

  const [supportCrossModalOpen, setSupportCrossModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (hasCrossingSizes && currentCrossingSize != null && !isCurrentCrossingValid) {
      onChange({ crossingSize: undefined });
    }
  }, [hasCrossingSizes, currentCrossingSize, isCurrentCrossingValid, onChange]);

  React.useEffect(() => {
    if (availableFontTypes.length > 0 && !availableFontTypes.includes(currentFontType)) {
      onChange({ fontType: effectiveFontType });
    }
  }, [availableFontTypes, currentFontType, effectiveFontType, onChange]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: createFontFacesCss() }} />
      <Paper
        elevation={0}
        style={{
          backgroundColor: w.paperBackground || theme.palette.background.paper,
          padding: 24,
          border: `1px solid ${w.borderLight || theme.palette.divider}`,
          borderRadius: w.borderRadius ?? theme.shape.borderRadius,
          boxShadow: w.shadowPaper || theme.shadows[1],
        }}
      >
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: 700,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            color: w.textPrimary || theme.palette.text.primary,
            fontSize: '0.95rem',
          }}
        >
          <span
            className="icon-settings"
            style={{ marginRight: 12, color: w.primary || theme.palette.primary.main, fontSize: '1.2rem' }}
          />
          {t('weha.globalSettings.title')}
        </Typography>
        <Box mb={2.5}>
          <CustomLabel>{t('weha.globalSettings.fontFamilyLabel')}</CustomLabel>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            value={fontFamily || (fontData[0]?.identifier ?? '')}
            onChange={(e) => {
              const newFontId = e.target.value;
              const changes: Partial<WehaGlobalSettings> = { fontFamily: newFontId };
              const newFont = getFontByIdentifier(fontData, newFontId);
              const firstMaterial = newFont?.materialTypes?.[0]?.identifier;
              if (firstMaterial && !newFont?.materialTypes?.some((m) => m.identifier === material)) {
                changes.material = firstMaterial;
              }
              const supported = getCommonFontTypesForLines(fontData, newFontId, lines);
              if (supported.length > 0 && !supported.includes(currentFontType)) {
                changes.fontType = resolveFontType(currentFontType, supported);
              }
              if (!newFont?.dowelXxl) {
                changes.extraLongDowel = false;
              }
              changes.crossingSize = undefined;
              onChange(changes);
            }}
            InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
            SelectProps={{
              renderValue: (value) => {
                const font = getFontByIdentifier(fontData, value as string);
                const displayName = font?.name ?? (value as string);
                const ff = getFontFamilyForIdentifier(value as string, appFont);
                return <span style={{ fontFamily: ff, fontWeight: 700 }}>{displayName}</span>;
              },
            }}
          >
            {fontData.map((f) => (
              <MenuItem
                key={f.identifier}
                value={f.identifier}
                style={{ fontFamily: getFontFamilyForIdentifier(f.identifier, appFont), fontWeight: 700 }}
              >
                {f.name}
              </MenuItem>
            ))}
          </TextField>
          {selectedFont?.patina?.value != null && selectedFont.patina.value !== '' && (
            <Typography
              variant="body2"
              style={{
                marginTop: 8,
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              {t('weha.globalSettings.patinaLabel')}: {selectedFont.patina.value}
            </Typography>
          )}
        </Box>
        {availableFontTypes.length > 0 && (
          <Box mb={2.5}>
            <CustomLabel>{t('weha.globalSettings.executionLabel')}</CustomLabel>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              value={effectiveFontType}
              onChange={(e) => onChange({ fontType: e.target.value })}
              InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
            >
              {executionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {materialTypes.length > 0 && (
          <Box mb={2.5}>
            <CustomLabel>{t('weha.globalSettings.materialLabel')}</CustomLabel>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              value={effectiveMaterial}
              onChange={(e) => onChange({ material: e.target.value })}
              InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
            >
              {materialTypes.map((m) => (
                <MenuItem key={m.identifier} value={m.identifier}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <Box mb={2.5}>
          <CustomLabel>{t('weha.globalSettings.alignmentLabel')}</CustomLabel>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            value={alignment}
            onChange={(e) => onChange({ alignment: e.target.value as 'left' | 'center' | 'right' })}
            InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
          >
            <MenuItem value="left">{t('weha.globalSettings.alignmentLeft')}</MenuItem>
            <MenuItem value="center">{t('weha.globalSettings.alignmentCenter')}</MenuItem>
            <MenuItem value="right">{t('weha.globalSettings.alignmentRight')}</MenuItem>
          </TextField>
        </Box>
        {hasCrossingSizes && (
          <Box mb={2.5}>
            <CustomLabel>{t('weha.globalSettings.supportCrossLabel')}</CustomLabel>
            <Box display="flex" alignItems="center" style={{ gap: 0 }} flexWrap="wrap">
              <Typography variant="body2" style={{ width: 120, marginRight: 12, fontWeight: 500 }}>
                {effectiveCrossingSize
                  ? formatCrossingSizeLabel(effectiveCrossingSize)
                  : t('weha.globalSettings.supportCrossNone')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setSupportCrossModalOpen(true)}
                style={{
                  borderRadius: w.borderRadius ?? theme.shape.borderRadius,
                  flexShrink: 0,
                }}
              >
                {t('weha.globalSettings.supportCrossSelectButton')}
              </Button>
            </Box>
          </Box>
        )}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                className={classes.root}
                checked={Boolean(globalSettings.dowelTemplate)}
                onChange={(_, checked) => onChange({ dowelTemplate: checked })}
                color="primary"
                disableRipple
                disableFocusRipple
                disableTouchRipple
              />
            }
            label={t('weha.globalSettings.dowelTemplate')}
          />
        </Box>
        {hasDowelXxl && (
          <Box mt={0}>
            <FormControlLabel
              control={
                <Checkbox
                  className={classes.root}
                  checked={extraLongDowel}
                  onChange={(_, checked) => onChange({ extraLongDowel: checked })}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  color="primary"
                />
              }
              label={t('weha.globalSettings.extraLongDowel')}
            />
          </Box>
        )}
        <Box mt={2.5}>
          <CustomLabel>{t('weha.globalSettings.commentLabel')}</CustomLabel>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={4}
            maxRows={4}
            value={localComment}
            onChange={(e) => {
              const v = e.target.value;
              setLocalComment(v.length <= 2000 ? v : v.slice(0, 2000));
            }}
            onBlur={() => onChange({ comment: localComment })}
            inputProps={{ maxLength: 2000 }}
            placeholder={t('weha.globalSettings.commentPlaceholder')}
            helperText={`${localComment.length}/2000`}
            InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
          />
        </Box>
      </Paper>

      {hasCrossingSizes && (
        <SelectCrossingSizeDialog
          open={supportCrossModalOpen}
          onClose={() => setSupportCrossModalOpen(false)}
          crossingSizes={crossingSizes}
          selectedCrossingSize={effectiveCrossingSize}
          onSelect={(size) => onChange({ crossingSize: size ?? undefined })}
          fontFamily={fontFamily}
          supportCrossCharacter={selectedFont?.supportCrossCharacter ?? '+'}
          onOpenChange={onCrossingSizeDialogOpenChange}
        />
      )}
    </>
  );
}
